import { db } from '../firebase';
import logger from '../utils/logger';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  orderBy,
  limit,
  startAfter,
  where,
  serverTimestamp
} from 'firebase/firestore';

/**
 * User Management Service
 * Handles user CRUD operations for system administrators
 */

/**
 * Get all users with pagination
 * @param {number} pageSize - Number of users per page
 * @param {DocumentSnapshot} lastDoc - Last document from previous page (for pagination)
 * @returns {Promise<Object>} Users array and last document
 */
export const getAllUsers = async (pageSize = 50, lastDoc = null) => {
  try {
    const usersRef = collection(db, 'users');
    let q = query(usersRef, orderBy('createdAt', 'desc'), limit(pageSize));

    if (lastDoc) {
      q = query(usersRef, orderBy('createdAt', 'desc'), startAfter(lastDoc), limit(pageSize));
    }

    const querySnapshot = await getDocs(q);
    const users = [];

    querySnapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data()
      });
    });

    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];

    return {
      users,
      lastDoc: lastVisible,
      hasMore: users.length === pageSize
    };
  } catch (error) {
    logger.error('Error fetching users:', error);
    throw error;
  }
};

/**
 * Search users by email or name
 * @param {string} searchTerm - Search term
 * @returns {Promise<Array>} Matching users
 */
export const searchUsers = async (searchTerm) => {
  try {
    const usersRef = collection(db, 'users');
    const querySnapshot = await getDocs(usersRef);

    const users = [];
    querySnapshot.forEach((doc) => {
      const userData = doc.data();
      const fullName = `${userData.firstName || ''} ${userData.lastName || ''}`.toLowerCase();
      const email = (userData.email || '').toLowerCase();
      const search = searchTerm.toLowerCase();

      if (fullName.includes(search) || email.includes(search)) {
        users.push({
          id: doc.id,
          ...userData
        });
      }
    });

    return users;
  } catch (error) {
    logger.error('Error searching users:', error);
    throw error;
  }
};

/**
 * Get users by family code
 * @param {string} familyCode - Family code
 * @returns {Promise<Array>} Users in family
 */
export const getUsersByFamily = async (familyCode) => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('familyCode', '==', familyCode));
    const querySnapshot = await getDocs(q);

    const users = [];
    querySnapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return users;
  } catch (error) {
    logger.error('Error fetching family users:', error);
    throw error;
  }
};

/**
 * Get system admins
 * @returns {Promise<Array>} System admin users
 */
export const getSystemAdmins = async () => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('role', 'in', ['root_admin', 'system_admin', 'support_agent', 'developer', 'ux_designer']));
    const querySnapshot = await getDocs(q);

    const admins = [];
    querySnapshot.forEach((doc) => {
      admins.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return admins;
  } catch (error) {
    logger.error('Error fetching system admins:', error);
    throw error;
  }
};

/**
 * Get user by ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} User data
 */
export const getUserById = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      return {
        id: userDoc.id,
        ...userDoc.data()
      };
    } else {
      throw new Error('User not found');
    }
  } catch (error) {
    logger.error('Error fetching user:', error);
    throw error;
  }
};

/**
 * Update user profile
 * @param {string} userId - User ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export const updateUserProfile = async (userId, updates) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    logger.error('Error updating user profile:', error);
    throw error;
  }
};

/**
 * Promote user to system admin
 * @param {string} userId - User ID
 * @param {string} role - System role (system_admin, support_agent, etc.)
 * @param {Object} permissions - System permissions
 * @returns {Promise<void>}
 */
export const promoteToSystemAdmin = async (userId, role, permissions) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      accountType: 'system_admin',
      role,
      systemPermissions: permissions,
      isSystemAdmin: true,
      promotedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    logger.error('Error promoting user to system admin:', error);
    throw error;
  }
};

/**
 * Update system admin permissions
 * @param {string} userId - User ID
 * @param {Object} permissions - Updated permissions
 * @returns {Promise<void>}
 */
export const updateSystemPermissions = async (userId, permissions) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      systemPermissions: permissions,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    logger.error('Error updating system permissions:', error);
    throw error;
  }
};

/**
 * Demote system admin to regular user
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
export const demoteSystemAdmin = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      role: null,
      systemPermissions: null,
      isSystemAdmin: false,
      demotedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    logger.error('Error demoting system admin:', error);
    throw error;
  }
};

/**
 * Deactivate user account
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
export const deactivateUser = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      isActive: false,
      deactivatedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    logger.error('Error deactivating user:', error);
    throw error;
  }
};

/**
 * Reactivate user account
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
export const reactivateUser = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      isActive: true,
      reactivatedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    logger.error('Error reactivating user:', error);
    throw error;
  }
};

/**
 * Get user statistics
 * @returns {Promise<Object>} User stats
 */
export const getUserStats = async () => {
  try {
    const usersRef = collection(db, 'users');
    const querySnapshot = await getDocs(usersRef);

    const stats = {
      totalUsers: 0,
      activeUsers: 0,
      systemAdmins: 0,
      usersWithFamily: 0,
      usersWithoutFamily: 0
    };

    querySnapshot.forEach((doc) => {
      const userData = doc.data();
      stats.totalUsers++;

      if (userData.isActive !== false) {
        stats.activeUsers++;
      }

      if (userData.role && ['root_admin', 'system_admin', 'support_agent', 'developer', 'ux_designer'].includes(userData.role)) {
        stats.systemAdmins++;
      }

      if (userData.familyCode) {
        stats.usersWithFamily++;
      } else {
        stats.usersWithoutFamily++;
      }
    });

    return stats;
  } catch (error) {
    logger.error('Error fetching user stats:', error);
    throw error;
  }
};
